// Customers > VIP Opt-Outs — the follow-up queue behind the opt-out briefing.
//
// Rachael, 2026-09-11: the briefing email went to Greg, her, Linda and Ashley at
// once, so it was addressed to everyone and owned by nobody. Each opt-out is now
// handed to one of Linda or Ashley by rotation and the email calls them out by
// name; this is where they log what came of it — contacted, did the customer
// reply, what reason they gave — and where "a running list of opt-out reasons and
// who converted them back" comes from.
//
// ⚠️ EVERY NUMBER ON THIS PAGE IS COMPUTED SERVER-SIDE in lib/vip-optout-followup.js.
// The page derives nothing, so it cannot drift from the email or from the rollup.
//
// ⚠️ "Not recorded" IS A REAL ROW IN THE REASON ROLLUP, never hidden. Early on it
// will be the biggest line, and dropping it would make the percentages read as
// though the list were complete when most of it has not been filled in yet.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Phone, RefreshCw, User, X } from 'lucide-react';
import { neonQuery } from '../services/api';

const OUTCOMES = [
  { v: 'won_back', label: 'Won back' },
  { v: 'staying_opted_out', label: 'Still leaving' },
  { v: 'no_response', label: 'Never replied' },
  { v: 'unreachable', label: 'Could not reach' },
];
const METHODS = [
  { v: 'phone', label: 'Phone' },
  { v: 'email', label: 'Email' },
  { v: 'text', label: 'Text' },
  { v: 'other', label: 'Other' },
];

const box = {
  border: '1px solid var(--gray-200)', borderRadius: 6, padding: '4px 8px',
  fontSize: 12, fontFamily: 'inherit', background: '#fff',
};

function fmtDate(d) {
  if (!d) return '';
  const [y, m, dd] = String(d).slice(0, 10).split('-');
  if (!y) return String(d);
  return new Date(Number(y), Number(m) - 1, Number(dd))
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function Pill({ on, children, onClick, tone = 'navy' }) {
  const c = tone === 'orange' ? 'var(--orange)' : 'var(--navy)';
  return (
    <button type="button" onClick={onClick} style={{
      padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
      borderRadius: 999, border: `1px solid ${on ? c : 'var(--gray-200)'}`,
      background: on ? c : '#fff', color: on ? '#fff' : '#444',
    }}>{children}</button>
  );
}

export default function VipOptOuts() {
  const [data, setData] = useState(null);
  const [rollup, setRollup] = useState(null);
  const [status, setStatus] = useState('open');
  const [assignee, setAssignee] = useState('all');
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [tab, setTab] = useState('queue');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const q = await neonQuery('vip_optout_queue', { status, assignee, days });
      setData(q);
      if (q && q.notReady) setErr(q.notReady);
    } catch (e) { setErr(e.message || 'Could not load the queue.'); }
    setLoading(false);
  }, [status, assignee, days]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab !== 'reasons') return;
    neonQuery('vip_optout_rollup', { days: 365 }).then(setRollup).catch(() => setRollup(null));
  }, [tab]);

  // ⚠️ `data.queue`, never `data.rows` - see the comment in neon.js. neonQuery()
  // unwraps a top-level `rows` key and throws the envelope away.
  const rows = (data && data.queue) || [];
  const owners = (data && data.owners) || [];
  const reasons = (data && data.reasons) || [];
  const counts = (data && data.counts) || {};

  const save = async (row, patch) => {
    setBusy(`${row.customer_id}:${row.event_date}`);
    try {
      await neonQuery('vip_optout_save', { customerId: row.customer_id, eventDate: row.event_date, patch });
      await load();
    } catch (e) { setErr(e.message || 'Could not save.'); }
    setBusy('');
  };

  const reassign = async (row, adminId) => {
    setBusy(`${row.customer_id}:${row.event_date}`);
    try {
      await neonQuery('vip_optout_reassign', { customerId: row.customer_id, eventDate: row.event_date, adminId });
      await load();
    } catch (e) { setErr(e.message || 'Could not reassign.'); }
    setBusy('');
  };

  const toggleOwner = async (o) => {
    try {
      const r = await neonQuery('vip_optout_owner_set', { adminId: o.admin_id, enabled: !o.enabled });
      setData((d) => ({ ...d, owners: r.owners }));
    } catch (e) { setErr(e.message || 'Could not change the rota.'); }
  };

  const rateLine = useMemo(() => {
    if (!counts || !counts.total) return null;
    return `${counts.closed || 0} of ${counts.total} closed · ${counts.won_back || 0} won back · ${counts.responded || 0} replied`;
  }, [counts]);

  return (
    <div className="page-content">
      <div className="breadcrumb"><span>Customers</span><span> / </span><a href="#top">VIP Opt-Outs</a></div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap', margin: '0 0 4px' }}>
        <h1 style={{ margin: 0, fontSize: 20, color: 'var(--navy)', fontWeight: 700 }}>VIP Opt-Outs</h1>
        {rateLine ? <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{rateLine}</span> : null}
        <button className="btn btn-ghost" onClick={load} style={{ marginLeft: 'auto', fontSize: 12 }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>
      <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--gray-500)', maxWidth: 780 }}>
        Each member who turns off auto-renew is handed to one person by rotation, and that person is
        named in the briefing email. Log what happened here. Nothing is filled in automatically:
        an untouched row means nobody has reached out yet.
      </p>

      {err ? (
        <div style={{ background: '#fdecea', border: '1px solid #f5c6c0', color: '#8a1c11', padding: '8px 12px', borderRadius: 6, fontSize: 13, margin: '0 0 12px' }}>{err}</div>
      ) : null}

      <div style={{ display: 'flex', gap: 6, margin: '0 0 12px' }}>
        <Pill on={tab === 'queue'} onClick={() => setTab('queue')}>Queue</Pill>
        <Pill on={tab === 'reasons'} onClick={() => setTab('reasons')}>Why they leave</Pill>
        <Pill on={tab === 'rota'} onClick={() => setTab('rota')}>Who is on rota</Pill>
      </div>

      {tab === 'queue' ? (
        <>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '0 0 10px' }}>
            {[['open', `Open${counts.open != null ? ` (${counts.open})` : ''}`],
              ['contacted', `Contacted${counts.contacted != null ? ` (${counts.contacted})` : ''}`],
              ['closed', `Closed${counts.closed != null ? ` (${counts.closed})` : ''}`],
              ['all', 'All']].map(([v, l]) => (
                <Pill key={v} on={status === v} onClick={() => setStatus(v)} tone="orange">{l}</Pill>
            ))}
            <span style={{ width: 12 }} />
            <Pill on={assignee === 'all'} onClick={() => setAssignee('all')}>Everyone</Pill>
            {owners.map((o) => (
              <Pill key={o.admin_id} on={assignee === o.admin_id} onClick={() => setAssignee(o.admin_id)}>
                {o.staff_name.split(' ')[0]}
              </Pill>
            ))}
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ ...box, marginLeft: 'auto' }}>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
          </div>

          {loading ? <div style={{ padding: 20, color: 'var(--gray-500)' }}>Loading…</div> : null}
          {!loading && !rows.length ? (
            <div style={{ padding: 20, color: 'var(--gray-500)', fontSize: 13 }}>
              Nothing here. {status === 'open' ? 'Every opt-out in this window has been followed up.' : 'No rows for this filter.'}
            </div>
          ) : null}

          {rows.map((r) => {
            const key = `${r.customer_id}:${r.event_date}`;
            const working = busy === key;
            return (
              <div key={key} className="card" style={{ marginBottom: 10, opacity: working ? 0.6 : 1 }}>
                <div className="card-body" style={{ padding: 12 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <strong style={{ color: 'var(--navy)', fontSize: 14 }}>{r.customer_name || `Customer ${r.customer_id}`}</strong>
                    <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>opted out {fmtDate(r.event_date)}</span>
                    {r.customer_email ? <a href={`mailto:${r.customer_email}`} style={{ fontSize: 12 }}>{r.customer_email}</a> : null}
                    {r.customer_phone ? <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{r.customer_phone}</span> : null}
                    <span style={{ marginLeft: 'auto', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--navy)', fontWeight: 600 }}>
                      <User size={12} /> {r.assignee_name || 'Unassigned'}
                    </span>
                    <select
                      value={r.assignee_admin_id || ''}
                      onChange={(e) => e.target.value && reassign(r, e.target.value)}
                      style={{ ...box, fontSize: 11 }}
                      title="Hand this to somebody else"
                    >
                      <option value="">Move to…</option>
                      {owners.map((o) => <option key={o.admin_id} value={o.admin_id}>{o.staff_name}</option>)}
                    </select>
                  </div>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
                    {r.contacted_at ? (
                      <>
                        <span style={{ fontSize: 12, color: '#1e7a3c', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Check size={13} /> Contacted{r.contacted_by_name ? ` by ${r.contacted_by_name}` : ''}
                        </span>
                        <button className="btn btn-ghost" style={{ fontSize: 11, padding: '2px 6px' }}
                          onClick={() => save(r, { contacted: false })} title="Undo">
                          <X size={11} />
                        </button>
                      </>
                    ) : (
                      <button className="btn btn-primary" style={{ fontSize: 12, padding: '4px 10px' }}
                        onClick={() => save(r, { contacted: true })}>
                        <Phone size={12} /> I contacted them
                      </button>
                    )}

                    <select value={r.contact_method || ''} onChange={(e) => save(r, { contactMethod: e.target.value })} style={box}>
                      <option value="">How…</option>
                      {METHODS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
                    </select>

                    <select value={r.responded || ''} onChange={(e) => save(r, { responded: e.target.value })} style={box}>
                      <option value="">Did they reply…</option>
                      <option value="yes">They replied</option>
                      <option value="no">No reply</option>
                    </select>

                    <select value={r.reason_code || ''} onChange={(e) => save(r, { reasonCode: e.target.value })} style={box}>
                      <option value="">Reason they gave…</option>
                      {reasons.map((x) => <option key={x.code} value={x.code}>{x.label}</option>)}
                    </select>

                    <select value={r.outcome || ''} onChange={(e) => save(r, { outcome: e.target.value })} style={box}>
                      <option value="">Outcome…</option>
                      {OUTCOMES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                  </div>

                  <input
                    defaultValue={r.reason_note || ''}
                    placeholder="In their own words (optional)"
                    onBlur={(e) => { if (e.target.value !== (r.reason_note || '')) save(r, { reasonNote: e.target.value }); }}
                    style={{ ...box, width: '100%', marginTop: 8, padding: '6px 8px' }}
                  />
                </div>
              </div>
            );
          })}
        </>
      ) : null}

      {tab === 'reasons' ? (
        <div className="card"><div className="card-body">
          <h2 style={{ margin: '0 0 4px', fontSize: 15, color: 'var(--navy)' }}>Why members say they are leaving</h2>
          <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '0 0 12px' }}>
            Last 12 months. &ldquo;Not recorded&rdquo; is shown rather than hidden, because until the
            logging is a habit it is the honest majority and leaving it out would overstate
            everything else.
          </p>
          {!rollup ? <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>Loading…</div> : (
            <>
              <table className="product-table" style={{ marginBottom: 20 }}>
                <thead><tr><th>Reason</th><th style={{ textAlign: 'right' }}>Members</th><th style={{ textAlign: 'right' }}>Won back</th></tr></thead>
                <tbody>
                  {rollup.reasons.map((x) => (
                    <tr key={x.code}>
                      <td>{x.label}</td>
                      <td style={{ textAlign: 'right' }}>{x.n}</td>
                      <td style={{ textAlign: 'right' }}>{x.won_back || ''}</td>
                    </tr>
                  ))}
                  {!rollup.reasons.length ? <tr><td colSpan={3} style={{ color: 'var(--gray-500)' }}>Nothing logged yet.</td></tr> : null}
                </tbody>
              </table>
              <h2 style={{ margin: '0 0 8px', fontSize: 15, color: 'var(--navy)' }}>By rep</h2>
              <table className="product-table">
                <thead><tr><th>Rep</th><th style={{ textAlign: 'right' }}>Assigned</th><th style={{ textAlign: 'right' }}>Contacted</th><th style={{ textAlign: 'right' }}>Replied</th><th style={{ textAlign: 'right' }}>Won back</th></tr></thead>
                <tbody>
                  {rollup.byRep.map((x) => (
                    <tr key={x.rep}>
                      <td>{x.rep}</td>
                      <td style={{ textAlign: 'right' }}>{x.assigned}</td>
                      <td style={{ textAlign: 'right' }}>{x.contacted}</td>
                      <td style={{ textAlign: 'right' }}>{x.responded}</td>
                      <td style={{ textAlign: 'right' }}>{x.won_back}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div></div>
      ) : null}

      {tab === 'rota' ? (
        <div className="card"><div className="card-body">
          <h2 style={{ margin: '0 0 4px', fontSize: 15, color: 'var(--navy)' }}>Who gets handed an opt-out</h2>
          <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '0 0 12px', maxWidth: 720 }}>
            Each new opt-out goes to whoever has gone longest without one, so it evens out on its own.
            Switch somebody off while they are away and the next ones go to the other person instead of
            piling up in a queue nobody is reading.
          </p>
          <table className="product-table">
            <thead><tr><th>Name</th><th>On rota</th><th style={{ textAlign: 'right' }}>Given so far</th><th>Last one</th></tr></thead>
            <tbody>
              {owners.map((o) => (
                <tr key={o.admin_id}>
                  <td>{o.staff_name}<div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{o.email}</div></td>
                  <td>
                    <Pill on={o.enabled} onClick={() => toggleOwner(o)} tone="orange">
                      {o.enabled ? 'On' : 'Off'}
                    </Pill>
                  </td>
                  <td style={{ textAlign: 'right' }}>{o.assigned_count}</td>
                  <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                    {o.last_assigned_at ? new Date(o.last_assigned_at).toLocaleDateString() : 'never'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
      ) : null}
    </div>
  );
}
